import { Flight } from '@mui/icons-material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { Alert, Box, Paper, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { MaritimeOrderInfo } from 'adoms-common-lib';
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { CustomerURLInformationUtil } from '../../common/customerURLInformationUtil';
import InfoRow from '../../components/atoms/InfoRow';
import { OrderBackNextButtons } from '../../components/atoms/OrderBackNextButtons';
import ApplicationTooBar from '../../components/molecule/ApplicationToolBar';
import MaritimeOrderGoods from '../../components/molecule/MaritimeOrderGoods';
import { MaritimeOrderRequesterInfo } from '../../components/molecule/MaritimeOrderRequesterInfo';
import OrderStepBar, { Process } from '../../components/molecule/OrderStepBar';
import { OrderInfoNotFound } from '../../components/organisms/OrderInfoNotFound';
import { SectionGrid } from '../../components/organisms/SectionGrid';
import { MaritimeOrderDestinationInfo } from '../../components/molecule/MaritimeOrderDestination';

export default function MaritimeOrderComplete() {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const order = (location.state as { order?: MaritimeOrderInfo } | null | undefined)?.order;

    const completeMessage =
        '配送依頼が完了しました。\n確認番号は配送状況の確認やお問い合わせの際に必要となります。大切に保管してください。';
    // URLIDコピーのためシャローコピー
    const customerURLInformation = { ...CustomerURLInformationUtil.instance.getCustomerURLInformation() };
    const orderViewPath = (customerURLInformation?.tenantId && order?.orderID)
        ? `/requesterview?tenantid=${customerURLInformation.tenantId}&orderid=${order.orderID}`
        : '';
    const displayValue = (value: string | undefined) => (value && value !== '' ? value : 'ー');

    useEffect(() => {
        if (customerURLInformation !== undefined) {
            CustomerURLInformationUtil.instance.deleteCustomerURLInformation();
        }
    });

    const handleNavigateTopPage = async () => {
        const urlId = customerURLInformation?.urlId;
        if (!urlId) {
            console.log("undefined urlId");
            navigate('/forbidden403Page');
            return;
        }
        await CustomerURLInformationUtil.instance.updateCustomerURLInformationByUrlId(urlId);
        navigate(`/${urlId}`);
    }

    const handleNavigateOrderViewPage = () => {
        if (!customerURLInformation?.tenantId) {
            navigate('/forbidden403Page');
            return;
        }
        if (!orderViewPath) {
            // ブラウザ更新等でstateが消えた場合など
            return;
        }
        navigate(orderViewPath);
    };

    if (!order) {
        return (
            <OrderInfoNotFound
                appBarLabel="配送依頼完了"
                process={Process.Complete}
                onNavigateHome={handleNavigateTopPage}
            />
        );
    }

    return (
        <Grid container justifyContent="center" xs={12} spacing={1}>
            <Grid item xs={12} sm={12} >
                <ApplicationTooBar label="配送依頼完了" />
            </Grid>
            <Grid item xs={12} sm={12} >
                <OrderStepBar process={Process.Complete} />
            </Grid>
            <Grid item xs={11} sm={11} mb={3}>
                <Alert severity="success" sx={{ border: '1px solid #e0e0e0', whiteSpace: 'pre-line' }}>{completeMessage}</Alert>
            </Grid>
            <SectionGrid title="確認番号" icon={<CheckCircleOutlineIcon />}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center', color: theme.palette.secondary.main }}>
                    {order?.orderID}
                </Typography>
            </SectionGrid>
            <SectionGrid title="フライト情報" icon={<Flight sx={{ rotate: "90deg" }} />}>
                <Paper sx={{
                    textAlign: 'center',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%'
                }} elevation={0}>
                    {/* 日付ヘッダー */}
                    <Typography sx={{ display: 'block', textAlign: 'center', mb: 1 }}>
                        {displayValue(order?.preferredDeliveryDate)}
                    </Typography>
                    <Grid container alignItems="center" justifyContent="center">
                        {/* 出発地と出発時間 */}
                        <Grid item xs={5} textAlign="center">
                            <Box>
                                <Typography variant="caption" sx={{ color: 'grey.500', display: 'block' }}>出発地</Typography>
                                <Typography variant="body1" sx={{ color: 'grey.800', mb: 0.5, fontWeight: 'bold' }}>{displayValue(order?.departure?.name)}</Typography>
                                <Typography>
                                    {displayValue(order?.preferredDeliveryTime?.split('-')?.[0])}
                                </Typography>
                            </Box>
                        </Grid>
                        {/* 矢印アイコン */}
                        <Grid item xs={2} textAlign="center">
                            <Flight sx={{ color: 'grey.500', fontSize: 24, rotate: '90deg' }} />
                        </Grid>
                        {/* 到着地と到着時間 */}
                        <Grid item xs={5} textAlign="center">
                            <Box>
                                <Typography variant="caption" sx={{ color: 'grey.500', display: 'block' }}>到着地</Typography>
                                <Typography variant="body1" sx={{ color: 'grey.800', mb: 0.5, fontWeight: 'bold' }}>{displayValue(order?.arrival?.name)}</Typography>
                                <Typography>
                                    {displayValue(order?.preferredDeliveryTime?.split('-')?.[1])}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </SectionGrid>
            <SectionGrid title="配送情報" icon={<Inventory2OutlinedIcon />}>
                <InfoRow label="配送ルート" value={order?.transportRoute} />
                <InfoRow label="補給本部伝票番号" value={order?.supplyHqVoucherNumber} />
                {order?.carrierWaybillNumber && (
                    <InfoRow label="日通運送状番号" value={order.carrierWaybillNumber} />
                )}
            </SectionGrid>
            <SectionGrid title="配送物" icon={<Inventory2OutlinedIcon />}>
                <MaritimeOrderGoods order={order} />
            </SectionGrid>
            <SectionGrid title="荷送人様の情報" icon={<LocalShippingOutlinedIcon />}>
                <MaritimeOrderRequesterInfo order={order} />
            </SectionGrid>
            <SectionGrid title="荷受人様の情報" icon={<DirectionsBoatIcon />}>
                <MaritimeOrderDestinationInfo order={order} />
            </SectionGrid>
            <OrderBackNextButtons
                backLabel='ホームに戻る'
                nextLabel='配送状況確認'
                onBack={() => handleNavigateTopPage()}
                onNext={() => handleNavigateOrderViewPage()}
            />
        </Grid>
    );
}